/**
 * The receipt of a just-in-time buy (BLU-1268): a photo taken at the counter,
 * or a file. Rides the existing PO files (`purchase_order_files`, type
 * 'invoice', the private bucket); no invoice records, no Xero deposit, which
 * is why the standard page's InvoicesCard is not rendered for this kind.
 *
 * "Take a photo" opens the phone camera (`capture="environment"`) and shows
 * only below `sm`; "Add a file" takes an image or a PDF everywhere. The hook
 * converts HEIC and downscales before upload. Opening a receipt fetches a
 * signed URL on the click (no thumbnails: one URL call per receipt on load is
 * not worth it for a list that is usually one photo).
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, FileText, Loader2, Paperclip, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionError } from '@/components/shared/SectionError';
import { formatDay, useDateFnsLocale } from '@/lib/format-dates';
import type { PurchaseOrderFile } from '../hooks/useJustInTimeOrder';

interface JitReceiptCardProps {
  files: PurchaseOrderFile[];
  loading: boolean;
  failed: boolean;
  retrying: boolean;
  onRetry: () => void;
  /** The viewer may add and delete receipts (and the PO is not cancelled). */
  canManage: boolean;
  uploading: boolean;
  deletingFileId: string | null;
  onUpload: (file: File) => Promise<boolean>;
  onOpen: (fileId: string) => void;
  onDelete: (fileId: string) => Promise<boolean>;
}

const formatSize = (bytes: number | null | undefined): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function JitReceiptCard({
  files,
  loading,
  failed,
  retrying,
  onRetry,
  canManage,
  uploading,
  deletingFileId,
  onUpload,
  onOpen,
  onDelete,
}: JitReceiptCardProps) {
  const { t } = useTranslation('orders');
  const dateLocale = useDateFnsLocale();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrderFile | null>(null);

  const handlePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires a change.
    event.target.value = '';
    if (file) void onUpload(file);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const done = await onDelete(deleteTarget.id);
    if (done) setDeleteTarget(null);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('jit.receipt.title')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('jit.receipt.help')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {t('jit.receipt.loading')}
          </p>
        ) : failed ? (
          <SectionError
            variant="compact"
            title={t('jit.receipt.failed')}
            onRetry={onRetry}
            retryLabel={t('jit.retry')}
            retrying={retrying}
          />
        ) : files.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('jit.receipt.none')}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {files.map((file) => {
              const size = formatSize(file.file_size);
              const deleting = deletingFileId === file.id;
              return (
                <li key={file.id} className="flex items-center gap-2 px-3 py-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => onOpen(file.id)}
                    className="min-w-0 flex-1 rounded-sm py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="block truncate text-sm text-primary-strong hover:underline">{file.file_name}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {[formatDay(file.created_at, dateLocale), size].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                  {canManage && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 shrink-0 text-muted-foreground hover:text-destructive sm:h-8 sm:w-8"
                      onClick={() => setDeleteTarget(file)}
                      disabled={deleting}
                      aria-label={t('jit.receipt.delete', { name: file.file_name })}
                    >
                      {deleting ? (
                        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                      ) : (
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="h-11 flex-1 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 sm:hidden"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Camera className="h-4 w-4" aria-hidden="true" />
              )}
              {t('jit.receipt.takePhoto')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1 gap-1.5 sm:h-9 sm:flex-none"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-busy={uploading || undefined}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Paperclip className="h-4 w-4" aria-hidden="true" />
              )}
              {uploading ? t('jit.receipt.uploading') : t('jit.receipt.addFile')}
            </Button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={handlePicked}
              data-testid="jit-receipt-camera"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={handlePicked}
              data-testid="jit-receipt-file"
            />
          </div>
        )}
      </CardContent>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => (open || deletingFileId ? undefined : setDeleteTarget(null))}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('jit.receipt.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="break-words">{deleteTarget?.file_name}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingFileId)}>{t('jit.dialog.back')}</AlertDialogCancel>
            <Button variant="destructive" onClick={() => void confirmDelete()} disabled={Boolean(deletingFileId)}>
              {deletingFileId && <Loader2 className="mr-1.5 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t('jit.receipt.deleteConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
